import { NGXLogger } from 'ngx-logger';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, from, throwError, firstValueFrom } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { BusinessPartner } from './business-partner/business-partner.component';
import { AppConstants } from 'src/app/commons/app-constants';
import { Page } from 'src/app/commons/models/page';
import { BusinessPartnerListDTO } from './business-partner-list/business-partner-list.component';
import { DetailCacheService } from 'src/app/offline/detail-cache.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { OutboxService, generateProvisionalId } from 'src/app/offline/outbox.service';
import { offlineDb, scopedKey } from 'src/app/offline/offline-db';
import { isOfflineCapableNative } from 'src/app/commons/utils/platform';
import { runDeltaListSync, evictStaleRowFromCache } from 'src/app/offline/delta-merge.util';
import { PrefetchGateService } from 'src/app/offline/prefetch-gate.service';
import { FULL_LIST_MAX, getFullList, saveFullList } from 'src/app/offline/offline-window';

// Skip caching anything bigger than this offline — a handful of large PDFs shouldn't blow out
// device storage during a background sync. Matches ProductService's 10MB attachment ceiling.
const MAX_CACHE_FILE_SIZE = 10 * 1024 * 1024;

export interface BpAttachment {
  id: number;
  originalFilename: string;
  extension: string;
  fileSize: number;
  contentType: string;
  url: string;
  thumbnailUrl?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  category?: string;
  note?: string;
  // local UI state (not from server)
  _deleting?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BusinessPartnerService {
  private readonly logger = inject(NGXLogger);
  private readonly prefetchGate = inject(PrefetchGateService);


  private apiUrl = AppConstants.API_URL + `api/v1/business-partners`; // Adjust base URL

  constructor(
    private http: HttpClient,
    private detailCacheService: DetailCacheService,
    private networkStatusService: NetworkStatusService,
    private outboxService: OutboxService,
  ) { }

  getById(encodedId: string): Observable<BusinessPartner> {
    this.logger.info('Service: Fetching partner by encodedId:', encodedId);
    return this.detailCacheService.cacheThrough(`bp:${encodedId}`, () =>
      this.http.get<BusinessPartner>(`${this.apiUrl}/${encodedId}`).pipe(
        tap(data => this.logger.info('Service: GET success:', data)),
        catchError(err => {
          this.logger.error('Service: GET error:', err);
          return throwError(() => err);
        })
      ),
      // Offline with no saved copy: its row from the full list saved on the phone (2026-09-29) — the form
      // opens with its values (read-only offline) instead of failing.
      async () => (await getFullList('businessPartners'))?.rows.find((r: any) => r?.encodedId === encodedId),
    );
  }

  create(payload: Partial<BusinessPartner>): Observable<BusinessPartner> {
    if (isOfflineCapableNative() && !this.networkStatusService.isOnline) {
      return from(this.createOffline(payload));
    }
    this.logger.debug('Service: Creating partner, payload:', JSON.stringify(payload, null, 2));
    return this.http.post<BusinessPartner>(this.apiUrl, payload).pipe(
      tap(res => this.logger.info('Service: CREATE success:', res)),
      catchError(err => {
        this.logger.error('Service: CREATE error:', err);
        return throwError(() => err);
      })
    );
  }

  // Fabricates a response shaped like a real BP so callers (the form's submit handler, which
  // just navigates to the list) work unmodified. The actual create is queued for
  // SyncEngineService to replay once back online. Also merges the fabricated BP directly into
  // the offline masterCache buyers/suppliers lists (per partyType) so it shows up immediately
  // in the TU form's autocomplete — the actual point of offline BP creation.
  private async createOffline(payload: Partial<BusinessPartner>): Promise<BusinessPartner> {
    const clientUuid = uuidv4();
    const provisionalId = generateProvisionalId();
    const provisionalEncodedId = `offline-${clientUuid}`;

    const fakeResponse: any = {
      ...payload,
      id: provisionalId,
      encodedId: provisionalEncodedId,
      // Not part of the real DTO — internal marker so UI code can show an "unsynced" badge.
      _offlineClientUuid: clientUuid,
    };

    await offlineDb.detailCache.put({ key: scopedKey(`bp:${provisionalEncodedId}`), data: fakeResponse, fetchedAt: Date.now() });
    await this.mergeIntoMasterCache(fakeResponse);

    const outboxPayload = { ...payload, clientUuid };
    await this.outboxService.enqueue('CREATE_BP', clientUuid, outboxPayload);
    return fakeResponse;
  }

  private async mergeIntoMasterCache(bp: any): Promise<void> {
    const keys: string[] = [];
    if (bp.partyType === 'BUYER' || bp.partyType === 'BOTH') keys.push('buyers');
    if (bp.partyType === 'SELLER' || bp.partyType === 'BOTH') keys.push('suppliers');
    for (const key of keys) {
      // scopedKey, not the bare name: MasterCacheService writes and merge-protects these lists
      // under the tenant-scoped key, so appending here under the raw one both risked serving the
      // row to a different tenant AND meant this offline-created partner was dropped by the next
      // dropdown refresh, which only preserves _offlineClientUuid rows it can actually see.
      const cacheKey = scopedKey(key);
      const entry = await offlineDb.masterCache.get(cacheKey);
      const list = Array.isArray(entry?.data) ? (entry!.data as any[]) : [];
      await offlineDb.masterCache.put({ key: cacheKey, data: [...list, bp], fetchedAt: entry?.fetchedAt ?? Date.now() });
    }
  }

  update(encodedId: string, payload: Partial<BusinessPartner>): Observable<BusinessPartner> {
    this.logger.debug('Service: Updating partner:', encodedId, 'payload:', JSON.stringify(payload, null, 2));
    return this.http.put<BusinessPartner>(`${this.apiUrl}/${encodedId}`, payload).pipe(
      tap(res => this.logger.info('Service: UPDATE success:', res)),
      catchError(err => {
        this.logger.error('Service: UPDATE error:', err);
        return throwError(() => err);
      })
    );
  }

  delete(encodedId: string): Observable<void> {
    this.logger.debug('Service: Deleting partner:', encodedId);
    return this.http.delete<void>(`${this.apiUrl}/${encodedId}`).pipe(
      tap(() => this.logger.info('Service: DELETE success')),
      catchError(err => {
        this.logger.error('Service: DELETE error:', err);
        return throwError(() => err);
      })
    );
  }

  checkDuplicateWarning(name?: string, taxId?: string, encodedId?: string): Observable<{ nameDuplicate: boolean; taxIdDuplicate: boolean }> {
    let params = new HttpParams();
    if (name) params = params.set('name', name);
    if (taxId) params = params.set('taxId', taxId);
    if (encodedId) params = params.set('encodedId', encodedId);

    this.logger.debug('Service: Checking BP duplicate warning with params:', params.toString());
    return this.http.get<{ nameDuplicate: boolean; taxIdDuplicate: boolean }>(`${this.apiUrl}/duplicate-warning`, { params });
  }

  // business-partner.service.ts 

  getAll(
    page: number,
    size: number,
    sortField: string,
    sortOrder: string,
    search: string,
    status: boolean | null
  ): Observable<Page<BusinessPartnerListDTO>> {
    let url = `${this.apiUrl}?page=${page}&size=${size}&sort=${sortField},${sortOrder}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status !== null) url += `&status=${status}`;
    this.logger.debug('[BP List] Request URL:', url); // ← Debug: check in browser console
    return this.http.get<Page<BusinessPartnerListDTO>>(url);
  }

  /** NEW – raw URL (used by the list component) */
  getAllRaw(url: string): Observable<Page<BusinessPartnerListDTO>> {
    return this.http.get<Page<BusinessPartnerListDTO>>(url);
  }

  // Delta-sync fetch (Increment 6): on first-ever sync (modifiedSince=null) requests exactly the
  // same page=0&size=10&sort=name,asc&search=&status=true as before, matching
  // BusinessPartnerListComponent.loadPartners()'s no-event default query, so the merged cache
  // stays a drop-in replacement for what that component reads offline. On a delta sync, size is
  // widened to comfortably capture every row touched since the last sync in one page, and
  // status is omitted entirely — see BusinessPartnerServiceImpl's modifiedSince override, which
  // deliberately ignores the caller's isActive filter on a delta call so a just-deactivated
  // (soft-deleted) BP still comes back (with isActive=false) for the merge to evict.
  private fetchBpPage(modifiedSince: string | null): Observable<HttpResponse<Page<BusinessPartnerListDTO>>> {
    let url = modifiedSince
      ? `${this.apiUrl}?page=0&size=200&sort=name,asc&search=&modifiedSince=${encodeURIComponent(modifiedSince)}`
      : `${this.apiUrl}?page=0&size=10&sort=name,asc&search=&status=true`;
    return this.http.get<Page<BusinessPartnerListDTO>>(url, { observe: 'response' });
  }

  // Called by OfflinePrefetchService (Android only) so the list's first page is already cached
  // before the user ever opens Business Partners. Delegates to runDeltaListSync (Increment 6) —
  // first sync ever caches the full response as-is; every sync after that merges only what's
  // changed into the existing cached top-10 window instead of replacing it wholesale. No
  // deletionsEntityType — BusinessPartner soft-deletes (isActive flips to false), it never
  // hard-deletes, so there's no tombstone table to consult (see mergeConfig.getIsActive below).
  // Returns a Promise (resolving once the LIST itself is fetched/cached — never rejects, this
  // is always best-effort) so OfflinePrefetchService's per-area progress checklist can show a
  // meaningful "done" instead of resolving instantly. Per-row detail/attachment prefetching
  // below stays fire-and-forget/background, same as ProductService's equivalent.
  // Phase 2 / D7 (OFFLINE_PLAN.md, 2026-09-29): the whole list (up to FULL_LIST_MAX rows, one request), so
  // this list screen can search / sort / page offline — before, offline showed only its first 10 rows.
  // Best-effort: a failure keeps the last saved list.
  private async saveFullListForOffline(): Promise<void> {
    if (!isOfflineCapableNative()) return;
    try {
      const res = await firstValueFrom(this.http.get<Page<BusinessPartnerListDTO>>(`${this.apiUrl}?page=0&size=${FULL_LIST_MAX}&sort=name,asc&search=`));
      if (Array.isArray(res?.content)) await saveFullList('businessPartners', res.content);
    } catch { /* keep the last saved list */ }
  }

  async prefetchDefaultList(): Promise<void> {
    await this.saveFullListForOffline();
    const cacheKey = `bpList:${this.apiUrl}?page=0&size=10&sort=name,asc&search=&status=true`;
    const result = await runDeltaListSync<BusinessPartnerListDTO>({
      entity: 'businessPartners',
      cacheKey,
      fetchPage: (modifiedSince) => this.fetchBpPage(modifiedSince),
      deletionsEntityType: null,
      mergeConfig: {
        getId: (bp) => bp.id,
        getCreatedDate: (bp) => bp.createdDate ?? '',
        getIsActive: (bp) => bp.isActive,
        windowSize: 10,
      },
      http: this.http,
    });
    // A deleted (or deactivated) BP's cached detail record is otherwise stranded in IndexedDB
    // forever once evicted from the list — see delta-merge.util.ts's evictedRows doc comment.
    // Attachment blobs (bpAttachment:${id}, keyed by attachment id) can't be derived without a
    // fetch this BP can no longer serve, so those are left as a small residual orphan (bounded
    // by the existing 10MB per-file cap) — only the detail record itself is reclaimed here.
    result?.evictedRows?.forEach(row => {
      if (isOfflineCapableNative() && row.encodedId) offlineDb.detailCache.delete(scopedKey(`bp:${row.encodedId}`));
    });
    // Every leaf HTTP call below goes through PrefetchGateService — bounded concurrency, run
    // outside Angular's zone, yielding between units. This used to fire a detail request +
    // an attachments-list + one blob per attachment for EVERY row synchronously with no
    // throttle; on a slow device with a flaky connection the clump of responses froze the app
    // (see PrefetchGateService's own comment). Still fire-and-forget — the gate just drains it
    // safely — so the per-area checklist row flips "done" at the same point as before.
    const rows = result?.page.content ?? [];
    for (const row of rows) {
      const encodedId = row.encodedId;
      if (encodedId) {
        // Raw http.get(), not getById()/DetailCacheService.cacheThrough — that method falls
        // back to a stale cached detail (or a generic non-HTTP Error) on ANY error whenever
        // isOfflineCapableNative(), even a real 404, which would swallow the exact signal this
        // self-heal needs. A raw get() gives the true HttpErrorResponse status; writing the
        // success path into detailCache under the same `bp:${encodedId}` key getById() reads is
        // a drop-in replacement for offline viewing.
        void this.prefetchGate.run(async () => {
          try {
            const data = await firstValueFrom(this.http.get<BusinessPartner>(`${this.apiUrl}/${encodedId}`));
            if (isOfflineCapableNative()) {
              await offlineDb.detailCache.put({ key: scopedKey(`bp:${encodedId}`), data, fetchedAt: Date.now() });
            }
          } catch (err) {
            // Server authoritatively says this row doesn't exist — evict it from the cached
            // window so it stops being retried forever.
            if ((err as HttpErrorResponse)?.status === 404) {
              evictStaleRowFromCache<BusinessPartnerListDTO>(cacheKey, row.id, (r) => r.id);
            }
          }
        });
      }
      this.cacheAttachmentsForRow(encodedId);
    }
  }

  // Caches every attachment's bytes (all file types, not images-only — an offline-cached PDF
  // is still useful even without a thumbnail) so BusinessPartnerComponent's attachment grid
  // renders offline instead of showing broken images/dead download links. Goes through the
  // /content proxy (never att.url/thumbnailUrl directly) for the same CORS reason as
  // ProductService.cachePhotoBlob — those fields are raw signed B2 URLs.
  private cacheAttachmentsForRow(encodedId: string): void {
    if (!encodedId) return;
    void this.prefetchGate.run(async () => {
      const attachments = await firstValueFrom(this.getAttachments(encodedId)).catch(() => null);
      if (attachments) this.cacheAttachmentBlobs(encodedId, attachments);
    });
  }

  // Called by BusinessPartnerComponent after its own (online) loadAttachments() succeeds — a
  // BP outside the last-10 prefetch window still gets its attachment blobs cached the moment
  // it's actually viewed, same "cache what's been seen" pattern as ProductComponent.loadPhotos()
  // calling ProductService.cachePhotosForProduct(). Without this, only the rare prefetch batch
  // would ever cache blobs, leaving every other BP's attachments permanently un-cacheable.
  cacheAttachmentBlobs(encodedId: string, attachments: BpAttachment[]): void {
    if (!isOfflineCapableNative()) return;
    attachments.forEach(att => {
      if (att.fileSize && att.fileSize > MAX_CACHE_FILE_SIZE) return;
      this.cacheAttachmentBlob(encodedId, att.id);
    });
  }

  // Also routed through the gate — BusinessPartnerComponent calls cacheAttachmentBlobs() after
  // its own online loadAttachments(), and a BP with a dozen attachments would otherwise fire a
  // dozen blob downloads at once the moment it is opened.
  private cacheAttachmentBlob(encodedId: string, docId: number): void {
    void this.prefetchGate.run(async () => {
      try {
        const blob = await firstValueFrom(
          this.http.get(`${this.apiUrl}/${encodedId}/attachments/${docId}/content`, { responseType: 'blob' }));
        await offlineDb.detailCache.put({ key: `bpAttachment:${docId}`, data: blob, fetchedAt: Date.now() });
      } catch { /* best-effort */ }
    });
  }

  deleteByEncodedIds(encodedIds: string[]): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/delete-multiple`, encodedIds);
  }

  // ── Attachments ─────────────────────────────────────────────────

  // Wrapped in DetailCacheService.cacheThrough — without this, even though the attachment
  // BLOBS get cached (cacheAttachmentBlob), there was no offline-available copy of the
  // attachment LIST metadata itself, so the component could never discover which blobs exist
  // to render offline (this is exactly the bug that made Product photos appear broken offline
  // earlier — same fix shape as ProductService.getProductPhotos()).
  getAttachments(encodedId: string): Observable<BpAttachment[]> {
    return this.detailCacheService.cacheThrough<BpAttachment[]>(`bpAttachments:${encodedId}`, () =>
      this.http.get<BpAttachment[]>(`${this.apiUrl}/${encodedId}/attachments`).pipe(
        tap(r => this.logger.debug('[BpDoc] getAttachments:', r)),
        catchError(err => { this.logger.error('[BpDoc] getAttachments error:', err); return throwError(() => err); })
      )
    );
  }

  uploadAttachment(encodedId: string, file: File, category?: string): Observable<HttpEvent<BpAttachment>> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (category) form.append('category', category);
    const req = new HttpRequest('POST', `${this.apiUrl}/${encodedId}/attachments`, form, {
      reportProgress: true,
      responseType: 'json'
    });
    return this.http.request<BpAttachment>(req);
  }

  deleteAttachment(encodedId: string, docId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${encodedId}/attachments/${docId}`).pipe(
      tap(() => this.logger.info('[BpDoc] deleteAttachment ok:', docId)),
      catchError(err => { this.logger.error('[BpDoc] deleteAttachment error:', err); return throwError(() => err); })
    );
  }

  // Bank account number reveal — added 2026-08-22, same audit-logged pattern as Employee
  // Onboarding's reveal endpoint. ADMIN or FINANCE_ACCESS only (enforced server-side too; this
  // call simply fails with 403 for anyone else — the UI hides the button separately, see
  // BusinessPartnerComponent.canRevealBank).
  revealBankAccountNumber(encodedId: string, bankDetailId: number): Observable<{ value: string }> {
    return this.http.get<{ value: string }>(`${this.apiUrl}/${encodedId}/bank-details/${bankDetailId}/reveal`).pipe(
      catchError(err => {
        this.logger.error('Service: reveal bank account number error:', err);
        return throwError(() => err);
      })
    );
  }

  toggleActive(encodedId: string, active: boolean): Observable<BusinessPartner> {
    return this.http.patch<BusinessPartner>(`${this.apiUrl}/${encodedId}/status`, { isActive: active }).pipe(
      tap(res => this.logger.info('Service: TOGGLE ACTIVE success:', res)),
      catchError(err => {
        this.logger.error('Service: TOGGLE ACTIVE error:', err);
        return throwError(() => err);
      })
    );
  }
}
